import {
  Action,
  Card,
  type CardProps,
  type CardVariantType,
  DataStatus,
  DropdownActionMenuItem,
} from 'platform/components';
import {Hide, Show} from 'platform/foundation';

import {ReactNode, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';

import {buildArray, composePath, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useEditCustomer} from '../../hooks/useEditCustomer';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {CustomerInformation} from './components/CustomerInformation';
import {CustomerMatchOrCreate} from './components/CustomerMatchOrCreate';
import type {CreateCustomerFormType} from './types/CreateCustomerFormType';
import {SecondStepComponentType} from './types/SecondStepComponentType';

export interface CustomerWidgetCardProps extends TestIdProps {
  customerId?: string | null;
  initialValues?: Partial<CreateCustomerFormType>;
  customTitle?: string;
  customActions?: DropdownActionMenuItem[];
  secondStepComponentType: SecondStepComponentType;
  onCustomer: (_customer: CustomerResponseBodyV2) => void;
  isLoadingOnCustomer?: boolean;
  isLoading?: boolean;
  onChange?: VoidFunction;
  resourceId?: string;
  recordId?: string;
  isDetailLinkVisible?: boolean;
  isContactPersonHidden?: boolean;
  hasVerification?: boolean;
  vehicleId?: string | Nullish;
  tooltip?: string;
  headerAction?: ReactNode;
  colorStripe?: CardProps['colorStripe'];
  control?: CardProps['control'];
  cardVariant?: CardVariantType;
  isEditingDisabled?: boolean;
  canChangeCustomer?: boolean;
  shouldWarnBeforeActiveBusinessCaseEdit?: boolean;
}

export function CustomerWidgetCard(props: CustomerWidgetCardProps) {
  const [isEditCustomerLoading, setIsEditCustomerLoading] = useState(false);
  const {
    data: customer,
    isLoading,
    isError,
  } = useGetCustomerV2Query({customerId: props.customerId ?? ''}, {skip: isNil(props.customerId)});
  const editCustomer = useEditCustomer(customer, props.onCustomer, {
    shouldWarnBeforeActiveBusinessCaseEdit: props.shouldWarnBeforeActiveBusinessCaseEdit,
  });

  const [canEditCustomer] = usePermissions({permissionKeys: ['editCustomer']});
  const isEditingDisabled = !canEditCustomer || props.isEditingDisabled;

  const handleEditCustomer = async () => {
    if (!editCustomer) {
      return;
    }

    setIsEditCustomerLoading(true);
    try {
      await editCustomer();
    } finally {
      setIsEditCustomerLoading(false);
    }
  };

  const actions = buildArray<Action>()
    .when(isNotNil(props.headerAction), {
      type: 'custom',
      component: props.headerAction,
    })
    .when(isNotNilOrEmpty(props.customActions), {
      type: 'dropdown-iconButton',
      icon: 'navigation/more_vert',
      severity: 'informational',
      menuItems: props.customActions ?? [],
    })
    .when(props.isDetailLinkVisible, {
      title: i18n.t('general.labels.detail'),
      leftIcon: 'action/open_in_new',
      variant: 'link',
      type: 'button',
      onClick: () =>
        window.open(
          composePath(customerRoutes.detail, {
            params: {id: props.customerId},
            isSearchParamsPreserved: false,
          })
        ),
    })
    .when(isNotNil(props.onChange), {
      variant: 'link',
      type: 'button',
      title: i18n.t('general.actions.change'),
      isDisabled: props.canChangeCustomer === false,
      leftIcon: 'action/autorenew',
      onClick: props.onChange,
    })
    .add({
      variant: 'link',
      type: 'button',
      title: i18n.t('general.actions.edit'),
      leftIcon: 'image/edit',
      isLoading: props.isLoadingOnCustomer || isEditCustomerLoading,
      isDisabled: props.canChangeCustomer === false || isEditingDisabled,
      tooltip: isEditingDisabled ? i18n.t('general.labels.noPermission') : undefined,
      onClick: handleEditCustomer,
    });

  return (
    <Card
      variant={props.cardVariant}
      actions={isNotNil(props.customerId) ? actions : undefined}
      title={props.customTitle || i18n.t('entity.customer.labels.customer')}
      tooltip={props.tooltip}
      colorStripe={props.colorStripe}
      control={props.control}
      data-testid={suffixTestId('header', props)}
    >
      <DataStatus isLoading={props.isLoading} minHeight={100}>
        <Hide when={props.customerId}>
          <CustomerMatchOrCreate
            onCustomer={props.onCustomer}
            initialValues={props.initialValues}
            data-testid={suffixTestId('finder', props)}
            vehicleId={props.vehicleId}
          />
        </Hide>
        <DataStatus isLoading={isLoading} isError={isError}>
          <Show when={customer}>
            <CustomerInformation
              secondStepComponentType={props.secondStepComponentType}
              isContactPersonHidden={props.isContactPersonHidden}
              isEditingDisabled={!canEditCustomer}
              customer={customer!}
              resourceId={props.resourceId}
              recordId={props.recordId}
              hasVerification={props.hasVerification}
              data-testid={suffixTestId('information', props)}
            />
          </Show>
        </DataStatus>
      </DataStatus>
    </Card>
  );
}
